import React, { useState } from 'react'
import { useMutation } from 'jsonapi-react'
import { Link } from 'react-router-dom'
import CateringAgreementModal from 'app/agreements/CateringAgreementModal'
import useSessionData from 'app/session/useSessionData'
import { LayoutLoggedOut as Layout } from 'app/theme/layout'
import Input from 'app/theme/forms/Input'
import Location from 'app/theme/forms/Location'
import Textarea from 'app/theme/forms/Textarea'
import Checkbox from 'app/theme/forms/Checkbox'
import Radio from 'app/theme/forms/Radio'
import { cleanPhone, formatPhone } from 'app/utils/phone'
import { isValidEmail } from 'lib/validation'
import styles from './GetStartedPage.module.css'

export default function GetStartedPage() {
  const [showAgreement, setShowAgreement] = useState(false)
  const [showSuccess, setShowSuccess] = useState(false)
  const [showError, setShowError] = useState(false)
  const [terms, setTerms] = useState(false)
  const [errors, setErrors] = useState({})
  const [sessionData, setSessionData] = useSessionData()

  const [submit, {
    data,
    setData,
  }] = useMutation('caterer_submissions', {
    initialData: {
      first_name: '',
      last_name: '',
      company: '',
      entity_name: '',
      email: '',
      phone: '',
      address_formatted: '',
      address: '',
      city: '',
      state: '',
      website: '',
      menu_url: '',
      facebook: '',
      yelp: '',
      instagram: '',
      twitter: '',
      about: '',
      service_type: null,
      meta: sessionData.meta
    },
  })

  const validate = (data) => {
    const errors = {}

    const regexPhone = RegExp('[0-9]{10,15}')

    if (!data.first_name) {
      errors.first_name = 'This field is required'
    }
    if (!data.last_name) {
      errors.last_name = 'This field is required'
    }
    if (!data.company) {
      errors.company = 'This field is required'
    }
    if (!data.entity_name) {
      errors.entity_name = 'This field is required'
    }
    if (!data.address) {
      errors.address = 'This field is required'
    }

    if (!data.about) {
      errors.about = 'This field is required'
    }
    if (!data.phone || !regexPhone.test(data.phone)) {
      errors.phone = 'Please enter a valid phone number'
    }
    if (!data.email || !isValidEmail(data.email)) {
      errors.email = 'Please enter a valid email'
    }
    if (!data.service_type) {
      errors.service_type = 'Please select a service type'
    }
    setErrors(errors)

    return !Object.keys(errors).length
  }

  const onAgreementLinkClick = event => {
    event.preventDefault()
    setShowAgreement(true)
  }

  const onSubmit = () => {
    const cleanedData = cleanData()
    
    if (!terms || !validate(cleanedData)) {
      return
    }

    if (showError) {
      setShowError(false)
    }

    delete data.address_formatted

    submit(data).then(res => {
      if (res.data) {
        setShowSuccess(true)
      } else {
        setShowError(true)
      }
    })
  }

  if (showSuccess) {
    // Use either SuccessView or SuccessViewCalendly
    return (
      <SuccessView />
    )
  }

  function cleanData() {
    const cleanedPhone = cleanPhone(data.phone)
    const cleanedData = { ...data, phone: cleanedPhone}
    setData(prev => ({
      ...prev,
      phone: cleanPhone(prev.phone)
    }))

    return cleanedData
  }

  return (
    <Layout className="py-16">
      <header className="mb-12">
        <h3 className="font-bold text-xl text-gray-400 text-center leading-tight">
          Get Started
        </h3>
        <h1 className="font-platform text-4xl text-gray-900 text-center leading-tight my-4">
          Let's get rolling
        </h1>

        <p className="text-gray-700 text-center leading-tight">
          Already signed up with us? Log in <Link to="/login" className='text-blue'>here.</Link>
          &nbsp; For support, email <a href="mailto:vendors@roaminghunger.com" className='text-blue'>vendors@roaminghunger.com</a>
        </p>

      </header>
      <section className="max-w-3xl px-6 m-auto">
        <p className="font-light text-2xl text-gray-900 mb-4">
          Your Contact Information
        </p>
        <div className="grid md:grid-cols-2 gap-5">
          <div>
            <label className="text-gray-900 font-bold text-md">First Name*</label>
            <Input
              placeholder="First Name"
              value={data.first_name}
              error={errors.first_name}
              className={errors.first_name && 'border-red'}
              onChange={first_name => {
                setData(prev => ({ ...prev, first_name }))
                if (errors.first_name) {
                  setErrors({ ...errors, first_name: null })
                }
              }}
            />
          </div>
          
          <div>
            <label className="text-gray-900 font-bold text-md">Last Name*</label>
            <Input
              placeholder="Last Name"
              value={data.last_name}
              error={errors.last_name}
              className={errors.last_name && 'border-red'}
              onChange={last_name => {
                setData(prev => ({ ...prev, last_name }))
                if (errors.last_name) {
                  setErrors({ ...errors, last_name: null })
                }
              }}
            />
          </div>

          <div className="md:col-span-2">
            <label className="text-gray-900 font-bold text-md">Food Truck or Brand Name*</label>
            <Input
              placeholder="Your Food Truck or Brand Name"
              value={data.company}
              error={errors.company}
              className={errors.company && 'border-red'}
              onChange={company => {
                setData(prev => ({ ...prev, company }))
                if (errors.company) {
                  setErrors({ ...errors, company: null })
                }
              }}
            />
          </div>

          <div className="md:col-span-2">
            <label className="text-gray-900 font-bold text-md">Business Entity Name (for Payment Processing)*</label>
            <p className="text-gray-700 font-light text-sm pb-2">Payments will be made to the name entered here. Enter the name as it appears on your W-9.</p>
            <Input
              placeholder="Business Entity Name (for Payment Processing)"
              value={data.entity_name}
              error={errors.entity_name}
              className={errors.entity_name && 'border-red'}
              onChange={entity_name => {
                setData(prev => ({ ...prev, entity_name }))
                if (errors.entity_name) {
                  setErrors({ ...errors, entity_name: null })
                }
              }}
            />
          </div>

          <div>
            <label className="text-gray-900 font-bold text-md">Email Address*</label>
            <Input
              placeholder="Email"
              value={data.email}
              error={errors.email}
              className={errors.email && 'border-red'}
              onChange={email => {
                setData(prev => ({ ...prev, email }))
                if (errors.email) {
                  setErrors({ ...errors, email: null })
                }
              }}
            />
          </div>

          <div>
            <label className="text-gray-900 font-bold text-md">Phone Number*</label>
            <Input
              type="tel"
              placeholder="Phone Number"
              value={data.phone}
              error={errors.phone}
              className={errors.phone && 'border-red'}
              onChange={phone => {
                setData(prev => ({
                   ...prev,
                   phone: formatPhone(phone), 
                }))
                if (errors.phone) {
                  setErrors({ ...errors, phone: null })
                }
              }}
            />
          </div>

          <div className="md:col-span-2">
            <label className="text-gray-900 font-bold text-md">Business Address*</label>
            <Location
              value={data.address_formatted}
              error={errors?.address && 'Please provide a valid address'}
              onChange={(address_formatted, address) => {
                setData(data => ({
                  ...data,
                  address_formatted: address_formatted,
                  address: address?.street,
                  city: address?.city_name,
                  state: address?.region_code
                }))
              }}
              placeholder="Enter an address"
            />
          </div>

          <div>
            <label className="text-gray-900 font-bold text-md">Website</label>
            <Input
              placeholder="Website URL"
              value={data.website}
              error={errors.website}
              className={errors.website && 'border-red'}
              onChange={website => {
                setData(prev => ({ ...prev, website }))
                if (errors.website) {
                  setErrors({ ...errors, website: null })
                }
              }}
            />
          </div>

          <div>
            <label className="text-gray-900 font-bold text-md">Menu Link</label>
            <Input
              placeholder="Menu Link"
              value={data.menu_url}
              error={errors.menu_url}
              className={errors.menu_url && 'border-red'}
              onChange={menu_url => {
                setData(prev => ({ ...prev, menu_url }))
                if (errors.menu_url) {
                  setErrors({ ...errors, menu_url: null })
                }
              }}
            />
          </div>

          <div>
            <label className="text-gray-900 font-bold text-md">Facebook</label>
            <Input
              placeholder="Facebook Profile Link"
              value={data.facebook}
              onChange={facebook => {
                setData(prev => ({ ...prev, facebook }))
              }}
            />
          </div>

          <div>
            <label className="text-gray-900 font-bold text-md">Twitter</label>
            <Input
              placeholder="Twitter Profile Link"
              value={data.twitter}
              onChange={twitter => {
                setData(prev => ({ ...prev, twitter }))
              }}
            />
          </div>

          <div>
            <label className="text-gray-900 font-bold text-md">Instagram</label>
            <Input
              placeholder="Instagram"
              value={data.instagram}
              onChange={instagram => {
                setData(prev => ({ ...prev, instagram }))
              }}
            />
          </div>

          <div>
            <label className="text-gray-900 font-bold text-md">Yelp</label>
            <Input
              placeholder="Yelp"
              value={data.yelp}
              onChange={yelp => {
                setData(prev => ({ ...prev, yelp }))
              }}
            />
          </div>
        </div>

        <div className="mt-6">
          <p className="font-light text-2xl text-gray-900 mb-4">
            What is your primary service type?*
          </p>
          {errors?.service_type && <span className="text-sm text-red-400">Please select a service type</span>}
          <div className="my-2">
            <Radio
              name="service_type"
              label="Truck"
              checked={data.service_type === 'truck'}
              onChange={() => {
                setData({ ...data, service_type: 'truck' })
              }}
            />
          </div>
          <div className="my-2">
            <Radio
              name="service_type"
              label="Trailer"
              checked={data.service_type === 'trailer'}
              onChange={() => {
                setData({ ...data, service_type: 'trailer' })
              }}
            />
          </div>
          <div className="my-2">
            <Radio
              name="service_type"
              label="Cart"
              checked={data.service_type === 'cart'}
              onChange={() => {
                setData({ ...data, service_type: 'cart' })
              }}
            />
          </div>
          <div className="my-2">
            <Radio
              name="service_type"
              label="Pop-up"
              checked={data.service_type === 'popup'}
              onChange={() => {
                setData({ ...data, service_type: 'popup' })
              }}
            />
          </div>
          <div className="my-2">
            <Radio
              name="service_type"
              label="Traditional"
              checked={data.service_type === 'traditional'}
              onChange={() => {
                setData({ ...data, service_type: 'traditional' })
              }}
            />
          </div>
          <div className="my-2">
            <Radio
              name="service_type"
              label="Drop-off"
              checked={data.service_type === 'dropoff'}
              onChange={() => {
                setData({ ...data, service_type: 'dropoff' })
              }}
            />
          </div>
        </div>

        <div className="mt-6">
          <p className="font-light text-2xl text-gray-900 mb-4">
            How did you hear about us?
          </p>
          <div className="my-2">
          <Radio
            name="referral_type"
            label="Google / Internet Search"
            checked={data.referral_type === 'google'}
            onChange={() => {
              setData({ ...data, referral_type: 'google' })
            }}
          />
          </div>
          <div className="my-2">
          <Radio
            name="referral_type"
            label="Article"
            checked={data.referral_type === 'article'}
            onChange={() => {
              setData({ ...data, referral_type: 'article' })
            }}
          />
          </div>
          <div className="my-2">
          <Radio
            name="referral_type"
            label="From a Friend"
            checked={data.referral_type === 'friend'}
            onChange={() => {
              setData({ ...data, referral_type: 'friend' })
            }}
          />
          </div>
          <div className="my-2">
          <Radio
            name="referral_type"
            label="Other"
            checked={data.referral_type === 'other'}
            onChange={() => {
              setData({ ...data, referral_type: 'other' })
            }}
          />
          { data.referral_type === 'other' && (
            <div className="mt-2 pl-8">
            <Textarea
              rows={3}
              cols={30}
              placeholder="..."
              value={data.referral_other}
              onChange={referral_other => {
                setData(prev => ({ ...prev, referral_other }))
              }}
            />
            </div>
          )}
          </div>

        </div>

        <div className="mt-6">
          <p className="font-light text-2xl text-gray-900 mb-4">
            Tell us your story!*
          </p>
          <Textarea
            rows={6}
            cols={30}
            placeholder="Example: XYZ Truck started serving delicious x's in the streets of Springfield in 2020..."
            value={data.about}
            error={errors.about}
            className={errors.about && 'border-red'}
            onChange={about => {
              setData(prev => ({ ...prev, about }))
              if (errors.about) {
                setErrors({ ...errors, about: null })
              }
            }}
          />
        </div>
        <div className="mt-6">
          <Checkbox
            label={(
              <span className="font-light text-lg">
                <span>I agree to the </span>
                <a href="#" className="text-blue hover:text-underline" onClick={onAgreementLinkClick}>
                  Vendor Catering Agreement
                </a>
              </span>
            )}
            checked={terms}
            onChange={checked => {
              setTerms(checked)
            }}
          />
          {showError && (
            <p className="mt-6 text-center text-red">
              Sorry, your submission was not successful, please try again.
            </p>
          )}
          <p className="mt-6 text-center text-gray italic">
            * Field is required
          </p>
          <button
            children="Submit"
            onClick={onSubmit}
            className={[
              'font-platform text-xl btn btn-blue mt-10 py-2 px-8 block m-auto',
              !terms && 'opacity-75',
            ]}
          />
        </div>
      </section>
      <CateringAgreementModal
        isOpen={showAgreement}
        closeModal={() => {
          setShowAgreement(false)
        }}
      />
    </Layout>
  )
}

const SuccessView = () => {
  return (
    <Layout className="py-16 container">
      <h3 className="font-bold text-xl text-gray-400 text-center leading-tight">
        Get Started
      </h3>
      <h1 className="font-platform text-4xl text-gray-900 text-center leading-tight my-4">
        Thank you for your submission!
      </h1>
      <p className="mx-auto max-w-2xl text-xl font-light mt-3">
        We look forward to partnering with you to grow your business! We are reviewing your information and you will receive your new login information for your Roaming Hunger profile within 3 standard business days.
      </p>
    </Layout>
  )
}


// Disabled for now
const SuccessCalendlyView = () => {
  <Layout className="py-16 container">
    <header>
      <h3 className="font-bold text-xl text-gray-400 text-center leading-tight">
        Get Started
      </h3>
      <h1 className="font-platform text-4xl text-gray-900 text-center leading-tight"> 
        Next Step: Schedule a New Vendor Demo
      </h1>
      <p className={["mx-auto max-w-2xl text-xl font-light mt-3"]}>
        We'll show you how to create the most compelling profile so you can get 
        the most catering gigs possible, and answer any questions.
      </p>
    </header>

    <iframe 
      className={[styles.calendly, "my-6 lg:my-0"]} 
      width="100%"
      height="100%"
      frameBorder="0"
      title="Calendly Scheduling Page"
      style={{minWidth: "320px"}} 
      src="https://calendly.com/adam-rh/new-vendor-welcome" 
    />

    <p className="mx-auto max-w-2xl text-xl font-light mt-3">
      * Also, be sure to add vendors@roaminghunger.com to your contact list so 
      our emails don’t end up in your spam folder.
    </p>
  </Layout>
}
